import React, { useState, useMemo, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { HelpCategory, HelpData } from '../../lib/types';
import { trackHelpArticleViewed, trackHelpSearched, trackHelpContactClicked } from '../../analytics';
import helpData from '../../data/help.json';
import { SUPPORT_EMAIL } from '../../lib/emails';
import styles from './HelpCenter.module.scss';

function filterCategories(categories: HelpCategory[], query: string): HelpCategory[] {
  if (!query) {
    return categories;
  }
  const q = query.toLowerCase();
  return categories
    .map((cat) => ({
      ...cat,
      articles: cat.articles.filter((a) => {
        return a.question.toLowerCase().includes(q) || a.answer.toLowerCase().includes(q);
      }),
    }))
    .filter((cat) => cat.articles.length > 0);
}

export default function HelpCenter() {
  const { categories } = helpData as HelpData;
  const [searchQuery, setSearchQuery] = useState('');

  const filteredCategories = useMemo(() => {
    return filterCategories(categories, searchQuery.trim());
  }, [categories, searchQuery]);

  const searchDebounceRef = useRef<ReturnType<typeof setTimeout>>();

  const handleSearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      setSearchQuery(value);
      clearTimeout(searchDebounceRef.current);
      if (value.trim().length >= 3) {
        searchDebounceRef.current = setTimeout(() => {
          const results = filterCategories(categories, value.trim());
          const totalResults = results.reduce((sum, cat) => sum + cat.articles.length, 0);
          trackHelpSearched(value.trim(), totalResults);
        }, 400);
      }
    },
    [categories],
  );

  return (
    <div className={styles.page}>
      {/* Hero */}
      <div className={styles.hero}>
        <Link to="/rescue" className={styles.backLink}>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <polyline points="15 18 9 12 15 6" />
          </svg>
          Back to Portal
        </Link>
        <h1>Help Center</h1>
        <p className={styles.heroSubtitle}>Find answers to common questions about the Fi rescue portal.</p>
        <div className={styles.searchWrap}>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <circle cx="11" cy="11" r="8" />
            <line x1="21" y1="21" x2="16.65" y2="16.65" />
          </svg>
          <input type="text" value={searchQuery} onChange={handleSearchChange} placeholder="Search for help..." />
        </div>
      </div>

      {/* FAQ Content */}
      <div className={styles.content}>
        {filteredCategories.length > 0 ? (
          filteredCategories.map((cat) => (
            <div key={cat.name} className={styles.category}>
              <h2>{cat.name}</h2>
              {cat.articles.map((article) => (
                <details
                  key={article.question}
                  className={styles.faqItem}
                  onToggle={(e) => {
                    if ((e.target as HTMLDetailsElement).open) {
                      trackHelpArticleViewed(cat.name, article.question);
                    }
                  }}
                >
                  <summary>{article.question}</summary>
                  <div className={styles.answer}>{article.answer}</div>
                </details>
              ))}
            </div>
          ))
        ) : (
          <p className={styles.noResults}>No articles matching your search.</p>
        )}
      </div>

      {/* CTA */}
      <div className={styles.cta}>
        <h3>Still need help?</h3>
        <p>Our rescue support team is here for you.</p>
        <a href={`mailto:${SUPPORT_EMAIL}`} className={styles.ctaButton} onClick={() => trackHelpContactClicked()}>
          Email {SUPPORT_EMAIL}
        </a>
      </div>

      {/* Footer */}
      <footer className={styles.footer}>
        <Link to="/rescue/terms">Fi Rescue Program Terms of Service</Link>
      </footer>
    </div>
  );
}
